import safeGetAttribute from './safeGetAttribute';
import { INLINEIMAGE_ATTRIBUTE_ORIGINALSRC } from 'owa-inline-image-consts';
import { DatapointStatus } from 'owa-analytics';
import loadFromModelBProxy, { MODEL_B_STATUS_CODE } from './loadFromModelBProxy';
import { getAccessTokenforResourceAsLazy } from 'owa-tokenprovider';
import { format } from 'owa-string-utils';
import loadImageFromSrc from './loadImageFromSrc';
import { isConsumer } from 'owa-session-store';
import { getLogicalRing } from 'owa-config';
import createImageProxyDatapoint, {
    ImageProxyEndpoint,
    ImageProxyTelemetryConfig,
} from './createImageProxyDatapoint';

// Traffic types
const TRAFFIC_PRIMARY = 'p';
const TRAFFIC_SHADOWING = 's';

// User types
const USERTYPE_CONSUMER = 'c';
const USERTYPE_BUSINESS = 'b';

// Meaning of url params
// u - real image url
// t - auth token
// r - t(r)affic type, p: primary, s: shadowing
// s - u(s)er, c: consumer, b: business
const IMAGE_PROXY_FORMAT_URL =
    'https://{0}.services.web.outlook.com/proxy/?u={1}&t={2}&r={3}&s={4}';

// The resource url for model D image proxy.
// TODO: we will need to update it to match with IMAGE_PROXY_URL where it is really served
const IMAGE_PROXY_RESOURCE_URL = 'https://outlookimageproxy.azurewebsites.net';

const MODEL_D_CLIENT_ERROR_REASON = 'clientErrorReason';

const enum ModelDClientErrorReason {
    Unknown = 0,
    TokenPromiseRejected = 1,
    TokenLengthIsZero = 2,
    RenderFailed = 3,
    FetchFailed = 4,
}

function getImageProxySubdomain(): string {
    switch (getLogicalRing()) {
        case 'Dogfood':
            return 'imgproxy-sdf';
        default:
            return 'imgproxy-prod';
    }
}

export default async function loadFromModelDProxy(
    img: HTMLImageElement,
    shadowTraffic: boolean,
    telemetryConfig: ImageProxyTelemetryConfig
): Promise<boolean> {
    // This is img that will be used for loading model-D
    // In non-shadowing, it is the real img in DOM. In shadowing, it will be temp un-attached img
    let modelDImage = img;

    if (shadowTraffic) {
        // Wait for model B to finish before continuing
        await loadFromModelBProxy(img, telemetryConfig);

        // Shadowing, use a fake img for modelDImage
        modelDImage = document.createElement('img');
    }

    const originalSrc = safeGetAttribute(img, INLINEIMAGE_ATTRIBUTE_ORIGINALSRC);
    if (!originalSrc) {
        // Model-D can't work without originalSrc
        return false;
    }

    const modelDDatapoint = createImageProxyDatapoint(
        {
            endpoint: ImageProxyEndpoint.ModelD,
        },
        telemetryConfig
    );
    modelDDatapoint.addCustomProperty('isShadowTraffic', shadowTraffic);
    let [token, tokenPromise] = getAccessTokenforResourceAsLazy(IMAGE_PROXY_RESOURCE_URL);
    modelDDatapoint.addCustomProperty('tokenCache', !!token ? 'warm' : 'cold');
    if (!token) {
        try {
            token = await tokenPromise;
        } catch (e) {
            modelDDatapoint.addCustomProperty(
                MODEL_D_CLIENT_ERROR_REASON,
                ModelDClientErrorReason.TokenPromiseRejected
            );
            modelDDatapoint.endWithError(DatapointStatus.ClientError);
            return false;
        }
    }

    let tokenLength = token ? token.length : 0;

    // Only attempt to load when we have a token
    if (tokenLength > 0) {
        const proxySrc = format(
            IMAGE_PROXY_FORMAT_URL,
            getImageProxySubdomain(),
            encodeURIComponent(originalSrc),
            encodeURIComponent(token),
            shadowTraffic ? TRAFFIC_SHADOWING : TRAFFIC_PRIMARY,
            isConsumer() ? USERTYPE_CONSUMER : USERTYPE_BUSINESS
        );

        try {
            const response = await loadImageFromSrc(modelDImage, proxySrc);
            modelDDatapoint.addCustomProperty(MODEL_B_STATUS_CODE, response.status);

            if (response.renderSuccess) {
                modelDDatapoint.endWithSuccess();
                return true;
            } else if (response.status === 200) {
                modelDDatapoint.addCustomProperty(
                    MODEL_D_CLIENT_ERROR_REASON,
                    ModelDClientErrorReason.RenderFailed
                );
                modelDDatapoint.endWithError(DatapointStatus.ClientError);
            } else {
                modelDDatapoint.endWithError(DatapointStatus.ServerError);
            }
        } catch (e) {
            modelDDatapoint.addCustomProperty(
                MODEL_D_CLIENT_ERROR_REASON,
                ModelDClientErrorReason.FetchFailed
            );
            modelDDatapoint.endWithError(DatapointStatus.ClientError);
        }
    } else {
        modelDDatapoint.addCustomProperty(
            MODEL_D_CLIENT_ERROR_REASON,
            ModelDClientErrorReason.TokenLengthIsZero
        );
        modelDDatapoint.endWithError(DatapointStatus.ClientError);
    }

    return false;
}
